// src/components/EnerjiOdasiEditor/EnerjiOdasiEditor.jsx
import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import './EnerjiOdasiEditor.css';

import {
  closeEnergyRoom, confirmEnergyRoom, setClipRect, addPanel,
  updatePanelPosition, removePanel, setSelectedPanel,
  resetRoom, setWallClearance, setPanelDepth, setDrawingScale,
  setDimTextSize, setDimArrowSize, setDimExtInner, setDimExtOuter,
  selectClipRect, selectPanels, selectEnergyRoomStatus,
  selectSelectedPanelId, selectWallClearance,
  selectPanelDepth, selectDrawingScale, selectDimSettings,
  selectPanelConfig, selectPanelMeters, selectSeparateDistribution,
  selectSplitParts,
  setDimensionMode, addCustomDimension, removeCustomDimension,
  clearCustomDimensions,
  setCustomDimTextSize, setCustomDimArrowSize,
  setFrameMargin, selectFrameMargin,
  selectDimensionMode, selectCustomDimensions, selectCustomDimSettings,
  setClippedSegments as setClippedSegmentsAction,
} from '../../redux/energyRoomSlice';
import { selectActiveFloorPlan } from '../../redux/floorPlanSlice';
import { useEnerjiOdasiPreview } from '../../hooks/useEnerjiOdasiPreview';
import { clipFloorPlanGeometry } from '../../shared/geometry/lineClipping';
import { calculateTotalPanelSize } from '../../utils/panelSizeCalculator';
import { splitMetersIntoGroups } from '../../utils/generatePanoDetayV2';
import {
  findSnapWall, slideAlongWall, clampSlideToSegment, hitTestRotatedPanel,
  extractWallCorners, findBestDimSnap, computeProjOffset,
} from '../../shared/snap/wallSnapUtils';

const EnerjiOdasiEditor = () => {
  const dispatch = useDispatch();

  // Redux
  const status = useSelector(selectEnergyRoomStatus);
  const clipRect = useSelector(selectClipRect);
  const panels = useSelector(selectPanels);
  const selectedPanelId = useSelector(selectSelectedPanelId);
  const wallClearanceCm = useSelector(selectWallClearance);
  const panelDepthCm = useSelector(selectPanelDepth);
  const drawingScale = useSelector(selectDrawingScale);
  const dimSettings = useSelector(selectDimSettings);
  const activeFloorPlan = useSelector(selectActiveFloorPlan);
  const panelConfig = useSelector(selectPanelConfig);
  const panelMeters = useSelector(selectPanelMeters);
  const separateDistribution = useSelector(selectSeparateDistribution);
  const splitParts = useSelector(selectSplitParts);
  const dimensionMode = useSelector(selectDimensionMode);
  const customDimensions = useSelector(selectCustomDimensions);
  const customDimSettings = useSelector(selectCustomDimSettings);
  const frameMargin = useSelector(selectFrameMargin);

  // Local state
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(null);
  const [dragEnd, setDragEnd] = useState(null);
  const [panelDrag, setPanelDrag] = useState(null);
  const [clippedSegments, setClippedSegments] = useState([]);
  const [panelCounter, setPanelCounter] = useState(1);
  const panelDragRef = useRef(null);

  // Dimension creation flow (local/ephemeral state)
  // dimPhase: 'idle' → 'pickSecond' → 'pickOffset' → 'idle'
  const [dimPhase, setDimPhase] = useState('idle');
  const [dimP1, setDimP1] = useState(null);       // {x, y} first point (confirmed)
  const [dimP2, setDimP2] = useState(null);       // {x, y} second point (confirmed)
  const [dimPreview, setDimPreview] = useState(null); // {x, y} current mouse pos for preview
  const [snapIndicator, setSnapIndicator] = useState(null); // {x, y, type} active snap visual
  const [dimOrthoAxis, setDimOrthoAxis] = useState(null);  // 'h' | 'v' | null

  const canvasRef = useRef(null);
  const {
    scene, camera, renderer,
    clearScene, fitToContent,
    renderFloorPlan, renderClippedRoom,
    updateSelectionRect, screenToWorld,
  } = useEnerjiOdasiPreview(canvasRef);

  const ds = drawingScale; // shorthand

  // ─── Build panel definitions (handles split panels) ────
  // Returns array of { label, compartments, totalWidthCm, depthCm }
  const panelDefs = React.useMemo(() => {
    if (!panelConfig || !panelMeters || panelMeters.length === 0) return null;
    try {
      const isSplit = splitParts && splitParts.length >= 2;
      const meterGroups = isSplit
        ? splitMetersIntoGroups(panelMeters, splitParts, panelConfig)
        : [panelMeters];

      const defs = [];

      for (let gi = 0; gi < meterGroups.length; gi++) {
        const groupMeters = meterGroups[gi];
        if (groupMeters.length === 0) continue;
        const sizes = calculateTotalPanelSize(groupMeters, panelConfig);
        const compartments = [];

        // Distribution: attached to last part (or separate)
        const isLastPart = gi === meterGroups.length - 1;
        if (!separateDistribution && isLastPart) {
          compartments.push({ name: 'Dağıtım\nBölümü', widthCm: sizes.distributionSize.width });
        }
        compartments.push({ name: `Sayac\nBolumu${isSplit ? ` ${gi + 1}` : ''}`, widthCm: sizes.meterCompartmentSize.width });

        const totalWidthCm = compartments.reduce((s, c) => s + c.widthCm, 0);
        defs.push({
          label: isSplit ? `Pano Parca-${gi + 1}` : 'Pano',
          compartments,
          totalWidthCm,
          depthCm: panelDepthCm,
        });
      }

      // Separate distribution as its own panel
      if (separateDistribution) {
        const fullSizes = calculateTotalPanelSize(panelMeters, panelConfig);
        defs.push({
          label: 'Dağıtım',
          compartments: [{ name: 'Dağıtım\nBölümü', widthCm: fullSizes.distributionSize.width }],
          totalWidthCm: fullSizes.distributionSize.width,
          depthCm: panelDepthCm,
        });
      }

      return defs.length > 0 ? defs : null;
    } catch (e) {
      console.warn('[EnerjiOdasiEditor] Panel defs calc failed:', e);
      return null;
    }
  }, [panelConfig, panelMeters, splitParts, separateDistribution, panelDepthCm]);

  // ─── Reset dim creation state when mode turns off ────
  useEffect(() => {
    if (!dimensionMode) {
      setDimPhase('idle');
      setDimP1(null);
      setDimP2(null);
      setDimPreview(null);
      setSnapIndicator(null);
      setDimOrthoAxis(null);
    }
  }, [dimensionMode]);

  // ─── Wall corners for dimension snap ─────────────────
  const wallCorners = React.useMemo(() => {
    if (clippedSegments.length === 0) return [];
    return extractWallCorners(clippedSegments, 0.5);
  }, [clippedSegments]);

  // ─── Initial floor plan render ───────────────────────
  useEffect(() => {
    if (activeFloorPlan?.group && status === 'idle') {
      renderFloorPlan(activeFloorPlan.group);
    }
  }, [activeFloorPlan, status, renderFloorPlan]);

  // ─── Re-render when state changes ────────────────────
  useEffect(() => {
    if ((status === 'clipped' || status === 'placing') && clippedSegments.length > 0 && clipRect) {
      const skipFit = !!panelDragRef.current || dimPhase !== 'idle';
      renderClippedRoom(clippedSegments, panels, clipRect, ds, selectedPanelId, dimSettings, skipFit, {
        customDimensions,
        customDimCfg: customDimSettings,
        frameMargin,
        dimP1,
        dimPhase: dimPhase !== 'idle' ? dimPhase : null,
        dimP2,
        dimPreview,
        dimOrthoAxis,
        corners: dimensionMode ? wallCorners : null,
        snapIndicator: dimensionMode ? snapIndicator : null,
      });
    }
  }, [status, clippedSegments, panels, clipRect, ds, selectedPanelId, dimSettings, customDimSettings, customDimensions, frameMargin, dimPhase, dimP1, dimP2, dimPreview, dimOrthoAxis, dimensionMode, wallCorners, snapIndicator, renderClippedRoom]);

  // ─── Room info in real cm (from world coords) ────────
  // clipRect is in world coords. worldUnits * drawingScale = cm
  const roomWidthCm = clipRect ? ((clipRect.maxX - clipRect.minX) * ds).toFixed(0) : '—';
  const roomHeightCm = clipRect ? ((clipRect.maxY - clipRect.minY) * ds).toFixed(0) : '—';

  // ─── Mouse handlers (all in world coords) ────────────
  const getCanvasRect = useCallback(() => canvasRef.current?.getBoundingClientRect(), []);

  const handleMouseDown = useCallback((e) => {
    if (e.button !== 0) return;
    const rect = getCanvasRect();
    if (!rect) return;

    if (status === 'idle' || status === 'selecting') {
      const world = screenToWorld(e.clientX, e.clientY, rect);
      if (world) { setIsDragging(true); setDragStart(world); setDragEnd(world); }
    } else if (status === 'clipped' || status === 'placing') {
      const world = screenToWorld(e.clientX, e.clientY, rect);
      if (!world || !clipRect) return;

      // Dimension mode: three-click workflow
      if (dimensionMode) {
        const snapThreshold = 10 / ds;
        const snapped = findBestDimSnap(world.x, world.y, clippedSegments, panels, ds, snapThreshold);

        if (dimPhase === 'idle') {
          // Click 1: set first point
          setDimP1({ x: snapped.x, y: snapped.y });
          setDimP2(null);
          setDimPreview(null);
          setSnapIndicator(null);
          setDimPhase('pickSecond');
        } else if (dimPhase === 'pickSecond') {
          // Click 2: snap at raw position, determine ortho axis
          setDimP2({ x: snapped.x, y: snapped.y });
          const adx = Math.abs(snapped.x - dimP1.x);
          const ady = Math.abs(snapped.y - dimP1.y);
          setDimOrthoAxis(adx >= ady ? 'h' : 'v');
          setDimPreview(null);
          setSnapIndicator(null);
          setDimPhase('pickOffset');
        } else if (dimPhase === 'pickOffset') {
          // Click 3: confirm offset → finalize dimension
          const offset = computeProjOffset(dimP1, dimP2, world, dimOrthoAxis);
          dispatch(addCustomDimension({
            id: `dim_${Date.now()}`,
            x1: dimP1.x, y1: dimP1.y,
            x2: dimP2.x, y2: dimP2.y,
            offset,
            axis: dimOrthoAxis,
          }));
          // Reset for next dimension
          setDimPhase('idle');
          setDimP1(null);
          setDimP2(null);
          setDimPreview(null);
          setSnapIndicator(null);
          setDimOrthoAxis(null);
        }
        return;
      }

      // Hit test panels (center-based + rotation aware)
      const clickedPanel = panels.find(p => hitTestRotatedPanel(world.x, world.y, p, ds));

      if (clickedPanel) {
        dispatch(setSelectedPanel(clickedPanel.id));
        const drag = {
          panelId: clickedPanel.id,
          startX: world.x, startY: world.y,
          origX: clickedPanel.x, origY: clickedPanel.y,
          origRotation: clickedPanel.rotation || 0,
        };
        setPanelDrag(drag);
        panelDragRef.current = drag;
      } else {
        dispatch(setSelectedPanel(null));
      }
    }
  }, [status, screenToWorld, getCanvasRect, panels, clipRect, ds, dispatch, dimensionMode, dimPhase, dimP1, dimP2, dimOrthoAxis, clippedSegments]);

  const handleMouseMove = useCallback((e) => {
    const rect = getCanvasRect();
    if (!rect) return;

    // Dimension preview on mouse move
    if (dimensionMode && dimPhase !== 'idle') {
      const world = screenToWorld(e.clientX, e.clientY, rect);
      if (world) {
        if (dimPhase === 'pickSecond' && dimP1) {
          // Snap at raw mouse position (no ortho constraint on search)
          const snapThreshold = 10 / ds;
          const snapped = findBestDimSnap(world.x, world.y, clippedSegments, panels, ds, snapThreshold);
          setDimPreview({ x: snapped.x, y: snapped.y });
          setSnapIndicator(snapped.type !== 'free' ? snapped : null);
        } else if (dimPhase === 'pickOffset') {
          // Raw mouse position (offset computed in drawing function)
          setDimPreview(world);
          setSnapIndicator(null);
        }
      }
      return;
    }

    // Dimension mode idle: show snap indicator on hover for first click
    if (dimensionMode && dimPhase === 'idle') {
      const world = screenToWorld(e.clientX, e.clientY, rect);
      if (world) {
        const snapThreshold = 10 / ds;
        const snapped = findBestDimSnap(world.x, world.y, clippedSegments, panels, ds, snapThreshold);
        setSnapIndicator(snapped.type !== 'free' ? snapped : null);
      }
      return;
    }

    if (isDragging && dragStart) {
      const world = screenToWorld(e.clientX, e.clientY, rect);
      if (world) { setDragEnd(world); updateSelectionRect(dragStart, world); }
    } else if (panelDrag && clipRect) {
      const world = screenToWorld(e.clientX, e.clientY, rect);
      if (!world) return;

      const panel = panels.find(p => p.id === panelDrag.panelId);
      if (!panel) return;

      const halfW = (panel.totalWidthCm / ds) / 2;
      const halfD = (panel.depthCm / ds) / 2;
      const wc = wallClearanceCm / ds;
      const snapThreshold = 15 / ds; // 15 cm snap distance

      const dx = world.x - panelDrag.startX;
      const dy = world.y - panelDrag.startY;

      // 1. Tentative free position (center-based)
      const tentX = panelDrag.origX + dx;
      const tentY = panelDrag.origY + dy;

      // 2. Try to snap to a wall
      const snap = findSnapWall(tentX, tentY, clippedSegments, snapThreshold, halfD, wc);

      let newX, newY, newRotation;

      if (snap) {
        // 3. Snap found: lock perpendicular distance, slide along wall
        newRotation = snap.rotation;

        // Project mouse delta onto wall direction for sliding
        const slide = slideAlongWall(dx, dy, snap.wallDx, snap.wallDy);
        const slideX = panelDrag.origX + slide.dx;
        const slideY = panelDrag.origY + slide.dy;

        // Re-snap the slid position to get correct perpendicular offset
        const reSnap = findSnapWall(slideX, slideY, clippedSegments, snapThreshold * 2, halfD, wc);
        if (reSnap && reSnap.seg === snap.seg) {
          newX = reSnap.snapX;
          newY = reSnap.snapY;
        } else {
          newX = snap.snapX + slide.dx;
          newY = snap.snapY + slide.dy;
        }

        // Clamp to segment bounds
        const clamped = clampSlideToSegment(newX, newY, snap.seg, snap.wallDx, snap.wallDy, halfW);
        newX = clamped.x;
        newY = clamped.y;
      } else {
        // 4. No snap: free movement, rotation resets to 0
        newX = tentX;
        newY = tentY;
        newRotation = 0;
      }

      dispatch(updatePanelPosition({ id: panelDrag.panelId, x: newX, y: newY, rotation: newRotation }));
    }
  }, [isDragging, dragStart, panelDrag, screenToWorld, getCanvasRect, updateSelectionRect, clipRect, panels, wallClearanceCm, ds, clippedSegments, dispatch, dimensionMode, dimPhase, dimP1]);

  const handleMouseUp = useCallback(() => {
    if (isDragging && dragStart && dragEnd) {
      const minX = Math.min(dragStart.x, dragEnd.x);
      const maxX = Math.max(dragStart.x, dragEnd.x);
      const minY = Math.min(dragStart.y, dragEnd.y);
      const maxY = Math.max(dragStart.y, dragEnd.y);

      if (maxX - minX > 0.5 && maxY - minY > 0.5) {
        const rect = { minX, minY, maxX, maxY };
        if (activeFloorPlan?.group) {
          const segments = clipFloorPlanGeometry(activeFloorPlan.group, rect);
          setClippedSegments(segments);
          dispatch(setClippedSegmentsAction(segments));
        }
        dispatch(setClipRect(rect));
      }
      setIsDragging(false); setDragStart(null); setDragEnd(null);
      updateSelectionRect(null, null);
    }
    if (panelDrag) {
      setPanelDrag(null);
      panelDragRef.current = null;
    }
  }, [isDragging, dragStart, dragEnd, panelDrag, activeFloorPlan, dispatch, updateSelectionRect]);

  // ─── Panel management ────────────────────────────────
  const handleAddAllPanels = useCallback(() => {
    if (!clipRect || !panelDefs) return;
    const roomCx = (clipRect.minX + clipRect.maxX) / 2;
    const roomCy = (clipRect.minY + clipRect.maxY) / 2;

    // Place panels side by side with a small gap
    const gap = 5 / ds; // 5 cm gap between panels
    const totalAllW = panelDefs.reduce((s, d) => s + d.totalWidthCm / ds, 0) + gap * (panelDefs.length - 1);
    let startX = roomCx - totalAllW / 2;

    let counter = panelCounter;
    for (const def of panelDefs) {
      const pw = def.totalWidthCm / ds;
      // Center-based: x is center of this panel
      const cx = startX + pw / 2;
      dispatch(addPanel({
        id: `panel_${Date.now()}_${counter}`,
        label: def.label,
        compartments: def.compartments,
        totalWidthCm: def.totalWidthCm,
        depthCm: def.depthCm,
        x: cx,
        y: roomCy,
        rotation: 0,
      }));
      startX += pw + gap;
      counter++;
    }
    setPanelCounter(counter);
  }, [clipRect, panelDefs, ds, panelCounter, dispatch]);

  const handleResetSelection = useCallback(() => {
    dispatch(resetRoom());
    setClippedSegments([]); setPanelCounter(1);
    if (activeFloorPlan?.group) renderFloorPlan(activeFloorPlan.group);
  }, [dispatch, activeFloorPlan, renderFloorPlan]);

  const handleToggleDimensionMode = useCallback(() => {
    dispatch(setDimensionMode(!dimensionMode));
  }, [dispatch, dimensionMode]);

  // ─── ESC key handler ──────────────────────────────────
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && dimensionMode) {
        if (dimPhase !== 'idle') {
          // Cancel in-progress dimension, stay in dimension mode
          setDimPhase('idle');
          setDimP1(null);
          setDimP2(null);
          setDimPreview(null);
          setSnapIndicator(null);
          setDimOrthoAxis(null);
        } else {
          // Exit dimension mode entirely
          dispatch(setDimensionMode(false));
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [dimensionMode, dimPhase, dispatch]);

  const getCursorStyle = () => {
    if (isDragging) return 'crosshair';
    if (panelDrag) return 'grabbing';
    if (status === 'idle' || status === 'selecting') return 'crosshair';
    if (dimensionMode) return 'crosshair';
    return 'default';
  };

  const hasDefs = panelDefs && panelDefs.length > 0;

  return (
    <div className="eo-overlay" onMouseUp={handleMouseUp}>
      <div className="eo-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="eo-header">
          <h2>Enerji Odası Oluştur</h2>
          <button className="eo-close-btn" onClick={() => dispatch(closeEnergyRoom())}>&times;</button>
        </div>

        <div className="eo-body">
          {/* Preview */}
          <div className="eo-preview">
            <div className="eo-canvas-container" ref={canvasRef}
              onMouseDown={handleMouseDown} onMouseMove={handleMouseMove}
              style={{ cursor: getCursorStyle() }}
            >
              <div className="eo-canvas-info">
                {status === 'idle' && 'Sol tıklayıp sürükleyerek oda alanını seçin'}
                {status === 'selecting' && 'Secim yapiliyor...'}
                {(status === 'clipped' || status === 'placing') && !dimensionMode && 'Panolari surukleyerek konumlandirin'}
                {(status === 'clipped' || status === 'placing') && dimensionMode && dimPhase === 'idle' && 'Olcu icin birinci noktaya tiklayin'}
                {(status === 'clipped' || status === 'placing') && dimensionMode && dimPhase === 'pickSecond' && 'Ikinci noktaya tiklayin (Orto aktif)'}
                {(status === 'clipped' || status === 'placing') && dimensionMode && dimPhase === 'pickOffset' && 'Olcu cizgisi konumunu belirleyin'}
              </div>
            </div>
            <div className="eo-preview-actions">
              {(status === 'clipped' || status === 'placing') && (
                <>
                  <button className="eo-btn" onClick={handleResetSelection}>Yeniden Sec</button>
                  <button
                    className={`eo-btn ${dimensionMode ? 'active' : ''}`}
                    onClick={handleToggleDimensionMode}
                  >
                    Olcu Ekle
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="eo-sidebar">
            {/* Room */}
            <div className="eo-section">
              <div className="eo-section-title">Oda Bilgisi</div>
              <div className="eo-info-row">
                <span className="eo-info-label">Genislik:</span>
                <span className="eo-info-value">{roomWidthCm} cm</span>
              </div>
              <div className="eo-info-row">
                <span className="eo-info-label">Yukseklik:</span>
                <span className="eo-info-value">{roomHeightCm} cm</span>
              </div>
            </div>

            {/* Panel definitions from PanoDetayPanel */}
            {hasDefs && (
              <div className="eo-section">
                <div className="eo-section-title">Pano Parcalari</div>
                {panelDefs.map((def, i) => (
                  <div key={i} className="eo-info-row">
                    <span className="eo-info-label">{def.label}:</span>
                    <span className="eo-info-value">{def.totalWidthCm.toFixed(0)} cm</span>
                  </div>
                ))}
              </div>
            )}

            {/* Panels list */}
            <div className="eo-section">
              <div className="eo-section-title">Panolar</div>
              <button className="eo-btn eo-btn-primary" style={{ width: '100%', marginBottom: 8 }}
                onClick={handleAddAllPanels} disabled={!clipRect || !hasDefs}
              >
                Tum Panolari Ekle ({panelDefs ? panelDefs.length : 0})
              </button>
              {!hasDefs && (
                <div style={{ fontSize: 11, color: '#ff8844', marginBottom: 6 }}>
                  Pano Detay Panelinde sayac adedini ayarlayin
                </div>
              )}
              <div className="eo-panel-list">
                {panels.map((p) => (
                  <div key={p.id}
                    className={`eo-panel-item ${selectedPanelId === p.id ? 'selected' : ''}`}
                    onClick={() => dispatch(setSelectedPanel(p.id))}
                  >
                    <span>{p.label} ({p.totalWidthCm.toFixed(0)}x{p.depthCm} cm)</span>
                    <button className="eo-panel-remove"
                      onClick={(e) => { e.stopPropagation(); dispatch(removePanel(p.id)); }}
                    >&times;</button>
                  </div>
                ))}
              </div>
            </div>

            {/* Custom Dimensions */}
            {customDimensions.length > 0 && (
              <div className="eo-section">
                <div className="eo-section-title">Kullanici Olculeri</div>
                <div className="eo-panel-list">
                  {customDimensions.map((dim) => {
                    const distCm = (Math.hypot(dim.x2 - dim.x1, dim.y2 - dim.y1) * ds).toFixed(1);
                    return (
                      <div key={dim.id} className="eo-panel-item">
                        <span>{distCm} cm</span>
                        <button className="eo-panel-remove"
                          onClick={() => dispatch(removeCustomDimension(dim.id))}
                        >&times;</button>
                      </div>
                    );
                  })}
                </div>
                <button className="eo-btn eo-btn-danger" style={{ width: '100%', marginTop: 6 }}
                  onClick={() => dispatch(clearCustomDimensions())}
                >
                  Tumunu Temizle
                </button>

                {/* Custom Dimension Settings — inside the same conditional block */}
                <div style={{ marginTop: 10, borderTop: '1px solid #444', paddingTop: 8 }}>
                  <div className="eo-section-title" style={{ fontSize: 11 }}>Kullanici Olcu Ayarlari</div>
                  <div className="eo-info-row">
                    <span className="eo-info-label">Text Boyutu (cm):</span>
                    <input type="number" className="eo-input" value={customDimSettings.textSize}
                      min={1} max={40} step={1}
                      onChange={(e) => dispatch(setCustomDimTextSize(Number(e.target.value) || 8))}
                    />
                  </div>
                  <div className="eo-info-row">
                    <span className="eo-info-label">Ok Boyutu (cm):</span>
                    <input type="number" className="eo-input" value={customDimSettings.arrowSize}
                      min={0.5} max={20} step={0.5}
                      onChange={(e) => dispatch(setCustomDimArrowSize(Number(e.target.value) || 3))}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Settings */}
            <div className="eo-section">
              <div className="eo-section-title">Ayarlar</div>
              <div className="eo-info-row">
                <span className="eo-info-label">Pano Derinligi (cm):</span>
                <input type="number" className="eo-input" value={panelDepthCm}
                  min={10} max={100}
                  onChange={(e) => dispatch(setPanelDepth(Number(e.target.value)))}
                />
              </div>
              <div className="eo-info-row">
                <span className="eo-info-label">Duvar Boslugu (cm):</span>
                <input type="number" className="eo-input" value={wallClearanceCm}
                  min={0} max={50}
                  onChange={(e) => dispatch(setWallClearance(Number(e.target.value)))}
                />
              </div>
              <div className="eo-info-row">
                <span className="eo-info-label">Çizim Ölçeği (1:N):</span>
                <input type="number" className="eo-input" value={drawingScale}
                  min={1} max={200}
                  onChange={(e) => dispatch(setDrawingScale(Number(e.target.value) || 50))}
                />
              </div>
              <div className="eo-info-row">
                <span className="eo-info-label">Cerceve Ust (cm):</span>
                <input type="number" className="eo-input" value={frameMargin.top}
                  min={0} max={200} step={1}
                  onChange={(e) => dispatch(setFrameMargin({ top: Number(e.target.value) || 0 }))}
                />
              </div>
              <div className="eo-info-row">
                <span className="eo-info-label">Cerceve Alt (cm):</span>
                <input type="number" className="eo-input" value={frameMargin.bottom}
                  min={0} max={200} step={1}
                  onChange={(e) => dispatch(setFrameMargin({ bottom: Number(e.target.value) || 0 }))}
                />
              </div>
              <div className="eo-info-row">
                <span className="eo-info-label">Cerceve Sol (cm):</span>
                <input type="number" className="eo-input" value={frameMargin.left}
                  min={0} max={200} step={1}
                  onChange={(e) => dispatch(setFrameMargin({ left: Number(e.target.value) || 0 }))}
                />
              </div>
              <div className="eo-info-row">
                <span className="eo-info-label">Cerceve Sag (cm):</span>
                <input type="number" className="eo-input" value={frameMargin.right}
                  min={0} max={200} step={1}
                  onChange={(e) => dispatch(setFrameMargin({ right: Number(e.target.value) || 0 }))}
                />
              </div>
            </div>

            {/* Panel Dimension Settings */}
            <div className="eo-section">
              <div className="eo-section-title">Pano Olcu Ayarlari</div>
              <div className="eo-info-row">
                <span className="eo-info-label">Text Boyutu (cm):</span>
                <input type="number" className="eo-input" value={dimSettings.textSize}
                  min={1} max={20} step={0.5}
                  onChange={(e) => dispatch(setDimTextSize(Number(e.target.value) || 4))}
                />
              </div>
              <div className="eo-info-row">
                <span className="eo-info-label">Ok Boyutu (cm):</span>
                <input type="number" className="eo-input" value={dimSettings.arrowSize}
                  min={0.5} max={20} step={0.5}
                  onChange={(e) => dispatch(setDimArrowSize(Number(e.target.value) || 3))}
                />
              </div>
              <div className="eo-info-row">
                <span className="eo-info-label">Ic Uzanti (cm):</span>
                <input type="number" className="eo-input" value={dimSettings.extInner}
                  min={1} max={50} step={1}
                  onChange={(e) => dispatch(setDimExtInner(Number(e.target.value) || 4))}
                />
              </div>
              <div className="eo-info-row">
                <span className="eo-info-label">Dis Uzanti (cm):</span>
                <input type="number" className="eo-input" value={dimSettings.extOuter}
                  min={1} max={50} step={1}
                  onChange={(e) => dispatch(setDimExtOuter(Number(e.target.value) || 16))}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="eo-footer">
          <button className="eo-btn" onClick={() => dispatch(closeEnergyRoom())}>İptal</button>
          <button className="eo-btn eo-btn-primary" onClick={() => dispatch(confirmEnergyRoom())}>Tamam</button>
        </div>
      </div>
    </div>
  );
};

export default EnerjiOdasiEditor;
